/**
 * El plano de la ventana de Nukli, como cadena de SVG.
 *
 * Vive fuera del componente Vue porque lo pintan DOS cosas: la SPA
 * (`GuideFigure.vue`) y las páginas estáticas que genera
 * `scripts/prerender-guides.js` para los buscadores. Si el dibujo estuviera solo
 * en el `<template>`, la versión prerenderizada tendría que reimplementarlo y
 * los dos se irían separando al primer retoque.
 *
 * No es una captura: es un plano. Para quien abre la app por primera vez, saber
 * *dónde mirar* vale más que ver píxeles exactos — y un esquema aguanta claro,
 * oscuro, zoom al 200 % y un rediseño de la UI sin quedarse obsoleto.
 *
 * El SVG lleva su `<style>` dentro (clases con prefijo `nkf-`) para ser
 * autosuficiente: se puede volcar tal cual en cualquier HTML. Los colores salen
 * de las variables `--lp-*`, así que hereda el tema de la página que lo aloja.
 */

/** Zonas de la ventana, con los mismos nombres que usa la app real. */
export const ZONES = {
  window: { x: 8, y: 38, w: 984, h: 554 },
  rail: { x: 8, y: 38, w: 54, h: 554 },
  railTop: { x: 15, y: 84, w: 40, h: 120 },
  railBottom: { x: 15, y: 430, w: 40, h: 150 },
  mailboxes: { x: 68, y: 38, w: 160, h: 554 },
  list: { x: 234, y: 38, w: 236, h: 554 },
  search: { x: 240, y: 44, w: 190, h: 38 },
  compose: { x: 434, y: 44, w: 36, h: 38 },
  tabs: { x: 240, y: 84, w: 224, h: 24 },
  listRow: { x: 246, y: 190, w: 212, h: 62 },
  reader: { x: 476, y: 38, w: 276, h: 554 },
  readerHead: { x: 476, y: 38, w: 276, h: 56 },
  readerBody: { x: 484, y: 100, w: 260, h: 264 },
  suggested: { x: 486, y: 372, w: 256, h: 100 },
  replybar: { x: 486, y: 482, w: 256, h: 98 },
  ai: { x: 758, y: 38, w: 234, h: 554 },
  aiThread: { x: 764, y: 44, w: 222, h: 396 },
  aiInput: { x: 766, y: 446, w: 220, h: 136 },
};

/** Las cinco zonas que se rotulan a la vez en el modo "plano". */
export const OVERVIEW_ZONES = ['rail', 'mailboxes', 'list', 'reader', 'ai'];

const RAIL_ICONS = [92, 126, 160, 194, 228, 262, 440, 474, 508, 542];
const FOLDER_ROWS = [88, 112, 136, 160, 190, 214, 238, 262, 292, 316, 340];
const LIST_ROWS = [120, 190, 260, 330, 400, 470];
const READER_LINES = [110, 130, 150, 174, 194, 214, 238, 258, 278, 302, 322, 342];

const CSS = `
.nkf-window{fill:var(--lp-surface);stroke:var(--lp-line-strong);stroke-width:2}
.nkf-bar{fill:var(--lp-bg-subtle)}
.nkf-dot{fill:var(--lp-line-strong)}
.nkf-pane{fill:var(--lp-bg-subtle);stroke:var(--lp-line);stroke-width:1.5}
.nkf-chip{fill:var(--lp-line-strong)}
.nkf-line{fill:var(--lp-line-strong);opacity:.75}
.nkf-strong{fill:var(--lp-muted)}
.nkf-ink{fill:var(--lp-ink-2)}
.nkf-accent{fill:var(--lp-accent)}
.nkf-soft{fill:var(--lp-accent);opacity:.35}
.nkf-avatar{fill:var(--lp-accent);opacity:.45}
.nkf-field{fill:var(--lp-surface);stroke:var(--lp-line-strong);stroke-width:1.5}
.nkf-row{fill:var(--lp-surface)}
.nkf-row-on{fill:var(--lp-accent-weak);stroke:var(--lp-accent);stroke-width:1.5}
.nkf-aibox{fill:var(--lp-accent-weak);stroke:var(--lp-accent);stroke-width:1.5;stroke-dasharray:5 4}
.nkf-bub-u{fill:var(--lp-accent);opacity:.55}
.nkf-bub-a{fill:var(--lp-surface);stroke:var(--lp-line-strong);stroke-width:1.5}
.nkf-dim{opacity:.42}
.nkf-focus{fill:var(--lp-accent);fill-opacity:.1;stroke:var(--lp-accent);stroke-width:4}
.nkf-map{fill:var(--lp-accent);fill-opacity:.08;stroke:var(--lp-accent);stroke-width:2.5}
.nkf-pin{fill:var(--lp-accent)}
.nkf-pin-t{fill:#fff;font-family:'Space Grotesk',system-ui,sans-serif;font-weight:700;font-size:26px}
.nkf-pin-t--sm{font-size:20px}
`.trim();

function rect(x, y, w, h, r, cls) {
  return `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" class="${cls}"/>`;
}

function circle(cx, cy, r, cls) {
  return `<circle cx="${cx}" cy="${cy}" r="${r}" class="${cls}"/>`;
}

function escapeAttr(text) {
  return String(text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** El esquema de la ventana, sin marcas. */
function appSchematic() {
  const parts = [];

  parts.push(rect(2, 2, 996, 596, 18, 'nkf-window'));
  parts.push('<path d="M2 20a18 18 0 0 1 18-18h960a18 18 0 0 1 18 18v14H2Z" class="nkf-bar"/>');
  parts.push(circle(26, 20, 5, 'nkf-dot'), circle(44, 20, 5, 'nkf-dot'), circle(62, 20, 5, 'nkf-dot'));

  // Barra de iconos.
  parts.push(rect(8, 38, 54, 554, 12, 'nkf-pane'));
  parts.push(rect(21, 48, 28, 28, 9, 'nkf-accent'));
  RAIL_ICONS.forEach((y) => parts.push(rect(21, y, 28, 24, 8, 'nkf-chip')));

  // Buzones.
  parts.push(rect(68, 38, 160, 554, 12, 'nkf-pane'));
  parts.push(rect(82, 54, 80, 10, 5, 'nkf-strong'));
  FOLDER_ROWS.forEach((y, i) => {
    parts.push(rect(82, y, 14, 10, 3, 'nkf-chip'));
    parts.push(rect(104, y + 1, i % 3 === 0 ? 92 : 68, 8, 4, 'nkf-line'));
  });

  // Lista.
  parts.push(rect(234, 38, 236, 554, 12, 'nkf-pane'));
  parts.push(rect(246, 50, 180, 26, 13, 'nkf-field'));
  parts.push(circle(452, 63, 13, 'nkf-accent'));
  [44, 52, 44].forEach((w, i) => {
    parts.push(rect(246 + i * 58, 88, w, 16, 8, i === 0 ? 'nkf-soft' : 'nkf-chip'));
  });
  LIST_ROWS.forEach((y, i) => {
    parts.push(rect(246, y, 212, 62, 10, i === 1 ? 'nkf-row-on' : 'nkf-row'));
    parts.push(circle(266, y + 20, 10, 'nkf-avatar'));
    parts.push(rect(284, y + 12, 86, 8, 4, 'nkf-strong'));
    parts.push(rect(284, y + 27, 150, 7, 3.5, 'nkf-line'));
    parts.push(rect(284, y + 42, 110, 7, 3.5, 'nkf-soft'));
  });

  // Lector.
  parts.push(rect(476, 38, 276, 554, 12, 'nkf-pane'));
  parts.push(circle(500, 64, 13, 'nkf-avatar'));
  parts.push(rect(522, 54, 110, 9, 4.5, 'nkf-strong'));
  parts.push(rect(522, 70, 160, 8, 4, 'nkf-line'));
  READER_LINES.forEach((y) => parts.push(rect(492, y, y % 3 === 0 ? 200 : 244, 8, 4, 'nkf-line')));
  parts.push(rect(490, 376, 248, 92, 12, 'nkf-aibox'));
  parts.push(rect(504, 390, 96, 8, 4, 'nkf-accent'));
  [410, 432].forEach((y, i) => parts.push(rect(504, y, i === 1 ? 180 : 216, 14, 7, 'nkf-chip')));
  parts.push(rect(490, 486, 248, 90, 12, 'nkf-field'));
  parts.push(rect(504, 500, 60, 18, 9, 'nkf-ink'));
  parts.push(rect(572, 500, 76, 18, 9, 'nkf-accent'));
  parts.push(rect(656, 500, 52, 18, 9, 'nkf-chip'));
  parts.push(rect(504, 530, 204, 8, 4, 'nkf-line'));

  // Asistente.
  parts.push(rect(758, 38, 234, 554, 12, 'nkf-pane'));
  parts.push(rect(772, 52, 90, 10, 5, 'nkf-accent'));
  parts.push(rect(800, 86, 178, 34, 12, 'nkf-bub-u'));
  parts.push(rect(772, 132, 190, 58, 12, 'nkf-bub-a'));
  parts.push(rect(800, 202, 178, 26, 12, 'nkf-bub-u'));
  parts.push(rect(772, 240, 190, 72, 12, 'nkf-bub-a'));
  parts.push(rect(772, 452, 206, 124, 14, 'nkf-field'));
  parts.push(rect(786, 470, 150, 9, 4.5, 'nkf-line'));
  parts.push(rect(786, 544, 70, 16, 8, 'nkf-chip'));
  parts.push(circle(958, 552, 12, 'nkf-accent'));

  return parts.join('');
}

function pin(x, y, label, small) {
  return `<g transform="translate(${x}, ${y})">`
    + `<circle r="${small ? 19 : 26}" class="nkf-pin"/>`
    + `<text class="nkf-pin-t${small ? ' nkf-pin-t--sm' : ''}" dy="${small ? 7 : 9}" text-anchor="middle">${escapeAttr(label)}</text>`
    + '</g>';
}

/**
 * @param {object} opts
 * @param {string} [opts.zone]     clave de ZONES a resaltar
 * @param {number} [opts.step]     número del paso, para el globo
 * @param {boolean} [opts.overview] rotula las cinco zonas principales a la vez
 * @param {string} [opts.alt]      texto alternativo
 * @returns {string} SVG autosuficiente
 */
export function renderFigureSvg({ zone = '', step = 0, overview = false, alt = 'Nukli' } = {}) {
  const box = overview ? null : ZONES[zone] || null;
  const marks = [];

  if (overview) {
    OVERVIEW_ZONES.forEach((key, i) => {
      const z = ZONES[key];
      marks.push(rect(z.x, z.y, z.w, z.h, 12, 'nkf-map'));
      marks.push(pin(z.x + z.w / 2, z.y + z.h / 2, String(i + 1), true));
    });
  } else if (box) {
    marks.push(rect(box.x - 4, box.y - 4, box.w + 8, box.h + 8, 14, 'nkf-focus'));
    if (step) {
      // El globo va en la esquina de la zona, pero sin salirse de la ventana:
      // en el rail (pegado al borde) se mete hacia dentro.
      marks.push(pin(Math.max(34, box.x), Math.max(34, box.y), String(step), false));
    }
  }

  const dim = box ? ' class="nkf-dim"' : '';
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 600" role="img"'
    + ` aria-label="${escapeAttr(alt)}"`
    + ' style="display:block;width:100%;height:auto;border-radius:var(--lp-radius,16px);'
    + 'background:var(--lp-bg-subtle);border:1px solid var(--lp-line)">'
    + `<style>${CSS}</style>`
    + `<g${dim}>${appSchematic()}</g>`
    + marks.join('')
    + '</svg>';
}
